/**
 * Company-wide invoice register (24 #4, #5, #6): every base and VO invoice in one table — project (system
 * chip + client), invoice no., milestone, amount, submitted date with its age (red over 90 d, amber 31–90 d),
 * QuickBooks number or "Not synced", and a ⋯ menu (Mark as paid…, Copy payment reminder, Open project).
 * Views: Awaiting payment (oldest first) · Ready to raise (finished work not invoiced, biggest first — with "All not
 * raised", every scheduled milestone, as its secondary view) · Paid · All; Base / VO switch; aging chips filter.
 * Without ready-to-bill data the tab is "Not raised" as before.
 * A row opens the project's Invoicing tab (VO invoices: its Variations tab).
 * BUG-006: deleting needs an Admin's approval — the ⋯ menu has Request deletion… (editors) / Delete invoice… (admins),
 * and on a requested row Approve deletion… / Reject deletion… (admins) or Cancel deletion request (the requester); the
 * row shows "Deletion requested · <name>" and a "Deletion requests" view lists them while there are any.
 * QuickBooks Desktop (owner decision 9 Oct 2026, read only — QbDesktopMatch.js): with `qbMatches` (GET
 * /qb-desktop/invoice-matches) each row shows its QuickBooks match under the QuickBooks Online state, and the
 * "Differs from QuickBooks" chip keeps the rows QuickBooks disagrees with (paid / open there, amount, customer).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, Check, CircleCheck, Copy, FolderOpen, Info, ReceiptText, RefreshCw, SearchX, Zap } from 'lucide-react';
import api from '@/lib/api';
import { formatDate, formatMoney, formatNumber, formatTimeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { CardFoot, Dash, Pager, Segmented, SfCard, SystemChip, TableScroll, numClass, plural, tableClass, tdClass, thClass, useIsPhone } from './SfUi';
import { AGING, DELETION_VIEW, REGISTER_VIEWS, isReadyRow, paymentReminder, registerCounts, registerView, rowLink } from './registerModel';
import { DeletionRequestPill, useInvoiceDeletion } from './invoiceDeletion';
import { QbMatchChip, qbMatchDiffers, qbMatchKey } from './QbDesktopMatch';

const PAGE_SIZE = 8;
const n2 = (value) => formatNumber(value, { decimals: 2 });
const AGE_TONE = { over90: 'font-semibold text-status-red-ink', d31: 'font-semibold text-status-amber-ink', d0: 'text-ink-3' };
const AGING_DOT = { over90: 'bg-status-red', d31: 'bg-status-amber', d0: 'bg-[color:var(--ink-4)]' };
/** A register row's QuickBooks Desktop match (base: 'invoice:<id>', VO: 'variation_invoice:<id>'). */
const qbMatchOf = (matches, row) => (matches ? matches[qbMatchKey(row.id, row.kind === 'vo')] || null : null);

export function QuickBooksCell({ row }) {
  if (row.bucket === 'notStarted') return <Dash label="Not raised" />;
  if (row.qbNumber) {
    return (
      <span className="inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full border border-line bg-surface px-2 text-[11.5px] font-semibold text-ink-2" title="Synced to QuickBooks">
        <Check className="h-3 w-3 text-status-green-ink" aria-hidden="true" />
        <span className="font-mono">QB #{row.qbNumber}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-ink-3">
      <RefreshCw className="h-3 w-3" aria-hidden="true" /> Not synced
    </span>
  );
}

function SubmittedCell({ row }) {
  if (row.bucket === 'notStarted') return <span className="text-[12px] text-ink-3">Not raised</span>;
  if (row.bucket === 'received') {
    return row.paidOn ? (
      <span className="block leading-tight">
        <span className="block whitespace-nowrap text-[13px] text-ink">{formatDate(row.paidOn)}</span>
        <span className="block text-[12px] text-ink-3">paid</span>
      </span>
    ) : <Dash />;
  }
  if (!row.submittedOn) return <Dash label="Submitted date not recorded" />;
  const estimated = row.submittedSource === 'last_change';
  return (
    <span className="block leading-tight" title={estimated ? 'Submitted date not recorded for this older invoice — this is the day it was last changed' : undefined}>
      <span className="block whitespace-nowrap text-[13px] text-ink">{formatDate(row.submittedOn)}</span>
      <span className={cn('block text-[12px]', AGE_TONE[row.aging] || 'text-ink-3')}>
        {estimated ? '≈ ' : ''}{formatNumber(row.ageDays ?? 0)} d
      </span>
    </span>
  );
}

function ProjectCell({ row, showDeletion = false }) {
  return (
    <span className="block min-w-0">
      <span className="block truncate text-[13.5px] font-semibold text-ink">{row.projectName || 'Unknown project'}</span>
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5">
        <SystemChip system={row.systemType} className="h-[18px] min-w-[34px] text-[10px]" />
        {row.kind === 'vo' ? <span className="rounded bg-status-violet-bg px-1.5 text-[10.5px] font-bold text-status-violet-ink">VO</span> : null}
        {row.client ? <span className="truncate text-[12px] text-ink-3">{row.client}</span> : null}
      </span>
      {showDeletion && row.deletionRequest ? (
        <span className="mt-1 flex min-w-0">
          <DeletionRequestPill invoice={row.invoice} className="max-w-full truncate" testId={`register-deletion-pill-${row.id}`} />
        </span>
      ) : null}
    </span>
  );
}

/** BUG-006: the line under a requested row — "Deletion requested · Mona · 4 Oct 2026 · “why”" and who decides. */
function DeletionLine({ row, approver }) {
  const request = row.deletionRequest;
  const reason = String(request.reason || '').trim();
  return (
    <span className="flex min-w-0 items-center gap-2 text-[12px] text-ink-3">
      <DeletionRequestPill invoice={row.invoice} className="shrink-0" testId={`register-deletion-pill-${row.id}`} />
      {request.requested_at ? <span className="shrink-0 whitespace-nowrap">{formatDate(request.requested_at)}</span> : null}
      {reason ? <span className="min-w-0 truncate" title={reason}>“{reason}”</span> : null}
      <span className="ml-auto shrink-0 whitespace-nowrap">{approver ? 'Approve or reject in ⋯' : 'Waiting for an Admin'}</span>
    </span>
  );
}

/** "Mark as paid…" — confirmed; PUT the invoice's status (the shell's toast offers Undo). */
function useMarkPaid(onChanged) {
  const confirm = useConfirm();
  return async (row) => {
    const ok = await confirm({
      title: `Mark invoice ${row.reference || ''} as paid?`.replace('  ', ' '),
      subtitle: `${row.projectName} · ${row.milestone} · ${formatMoney(row.amount)}`,
      description: 'Its status becomes Payment Received and it moves to Paid. You can undo it from the message that follows.',
      confirmLabel: 'Mark as paid',
      icon: CircleCheck,
      onConfirm: async () => {
        await api.put(row.kind === 'vo' ? `/variation-invoices/${row.id}` : `/invoices/${row.id}`, { status: 'Payment Received' });
      },
    });
    if (!ok) return;
    notify.success(`Invoice ${row.reference || row.milestone} marked as paid`);
    await onChanged?.();
  };
}

async function copyText(value) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand?.('copy');
  area.remove();
}

/**
 * props: rows (buildRegisterRows, already filtered by the page), onChanged (reload after Mark as paid),
 * canEdit (Mark as paid), filtered (the page filters narrow the rows), onClearFilters;
 * optional controlled view / onViewChange and aging / onAgingChange (the header links open "older than 90 d");
 * readyIds — Set, or Map id → ready-to-bill item (evidence, project status) — for "Ready to raise" (hidden when null).
 * highlightId — the invoice a link points at (?invoice=<id>, e.g. from the deletion-request notification).
 * qbMatches — GET /qb-desktop/invoice-matches ({ matches, last_success_at, … }) or null when QuickBooks Desktop is
 * off / not read: then no chip and no "Differs from QuickBooks" filter.
 */
export function InvoiceRegister({
  rows: rowsProp, onChanged, canEdit = true, filtered = false, onClearFilters,
  view: viewProp, onViewChange, aging: agingProp, onAgingChange,
  readyIds = null, onRaise, highlightId = '', qbMatches = null,
}) {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const markPaid = useMarkPaid(onChanged);
  const deletion = useInvoiceDeletion({ onChanged });
  const applyDeletion = deletion.apply;
  // A request / approval shows on the row at once (the page reloads right after).
  const allRows = useMemo(() => rowsProp.map((row) => {
    const invoice = applyDeletion(row.invoice);
    if (invoice === row.invoice) return row;
    return invoice ? { ...row, invoice, deletionRequest: invoice.deletion_request || null } : null;
  }).filter(Boolean), [rowsProp, applyDeletion]);
  // QuickBooks Desktop (read only): "Differs from QuickBooks" narrows every view, like the page filters do.
  const qbMap = qbMatches?.matches || null;
  const [qbDiffersOnly, setQbDiffersOnly] = useState(false);
  const qbFilterOn = Boolean(qbMap) && qbDiffersOnly;
  const qbDiffersCount = useMemo(
    () => (qbMap ? allRows.filter((row) => qbMatchDiffers(qbMatchOf(qbMap, row))).length : 0),
    [allRows, qbMap]
  );
  const rows = useMemo(
    () => (qbFilterOn ? allRows.filter((row) => qbMatchDiffers(qbMatchOf(qbMap, row))) : allRows),
    [allRows, qbFilterOn, qbMap]
  );
  const [ownView, setOwnView] = useState('awaiting');
  const [ownAging, setOwnAging] = useState(null);
  const view = viewProp ?? ownView;
  const setView = onViewChange || setOwnView;
  const aging = agingProp === undefined ? ownAging : agingProp;
  const setAging = onAgingChange || setOwnAging;
  const [kind, setKind] = useState('all');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [view, aging, rows]);
  const counts = useMemo(() => registerCounts(kind === 'all' ? rows : rows.filter((row) => row.kind === kind), { readyIds }), [rows, kind, readyIds]);
  const kindCounts = useMemo(() => registerCounts(rows).kinds, [rows]);
  const list = useMemo(() => registerView(rows, { view, kind, aging: view === 'awaiting' ? aging : null, readyIds }), [rows, view, kind, aging, readyIds]);
  // Top tabs: "Ready to raise" stands for both not-invoiced views; without ready data it is plain "Not raised".
  // "Deletion requests" only while there are any (or while it is open), for people who may act on them.
  const views = [
    ...REGISTER_VIEWS.filter((item) => (readyIds ? item.key !== 'notStarted' : item.key !== 'ready')),
    ...(canEdit && (counts.deletion > 0 || view === DELETION_VIEW.key) ? [DELETION_VIEW] : []),
  ];
  const tabOf = (key) => (readyIds && key === 'notStarted' ? 'ready' : key);
  const pageCount = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const shown = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const total = list.reduce((sum, row) => sum + row.amount, 0);
  const hiddenAmount = list.slice(current * PAGE_SIZE).reduce((sum, row) => sum + row.amount, 0);

  const go = (setter) => (value) => { setter(value); setPage(1); };
  // Turning it on opens All invoices, so the rows listed are the ones its count says (the tabs then narrow them).
  const toggleQbDiffers = () => {
    const next = !qbDiffersOnly;
    setQbDiffersOnly(next);
    if (next) setView('all');
    setPage(1);
  };
  const openRow = (row) => { const to = rowLink(row); if (to) navigate(to); };
  const copyReminder = async (row) => {
    try {
      await copyText(paymentReminder(row));
      notify.success('Payment reminder copied', { description: `${row.reference ? `Invoice ${row.reference} · ` : ''}${row.client || row.projectName}` });
    } catch {
      notify.error('Could not copy the reminder', { description: 'Your browser blocked the clipboard.' });
    }
  };
  const isReady = (row) => isReadyRow(row, readyIds);
  const readyItem = (row) => (readyIds && typeof readyIds.get === 'function' ? readyIds.get(row.id) || null : null);
  const menu = (row) => [
    onRaise && row.kind === 'base' && row.bucket === 'notStarted' && { label: 'Raise invoice…', icon: ReceiptText, onSelect: () => onRaise(row), testId: `invoice-raise-${row.id}` },
    canEdit && row.bucket === 'awaiting' && { label: 'Mark as paid…', icon: CircleCheck, onSelect: () => markPaid(row), testId: `invoice-mark-paid-${row.id}` },
    row.bucket === 'awaiting' && { label: 'Copy payment reminder', icon: Copy, onSelect: () => copyReminder(row), testId: `invoice-copy-reminder-${row.id}` },
    rowLink(row) && { label: row.kind === 'vo' ? 'Open project variations' : 'Open project invoicing', icon: FolderOpen, onSelect: () => openRow(row) },
    ...deletion.menuItems(row.invoice, { canEdit, kind: row.kind, testIdPrefix: 'register' }),
  ];

  const tabs = (
    <div role="tablist" aria-label="Invoice views" className="-mb-px flex min-w-0 gap-4 overflow-x-auto">
      {views.map((item) => {
        const active = item.key === tabOf(view);
        const count = item.key === DELETION_VIEW.key ? counts.deletion : counts.views[item.key];
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => go(setView)(item.key)}
            data-testid={`register-view-${item.key}`}
            title={item.key === 'notStarted' ? 'Scheduled payment milestones that have not been invoiced yet'
              : item.key === 'ready' ? 'Milestones whose work is done in Execution and that are not invoiced yet'
                : item.key === 'deletion' ? 'Invoices someone asked to delete — an Admin approves or rejects each one' : undefined}
            className={cn(
              'relative inline-flex h-[50px] shrink-0 items-center gap-1.5 whitespace-nowrap text-[13.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'text-ink after:absolute after:inset-x-0 after:bottom-0 after:h-[2.5px] after:rounded-t after:bg-brand' : 'text-ink-3 hover:text-ink-2'
            )}
          >
            {item.label}
            {item.key !== 'all' ? (
              <span className={cn('rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums', active ? 'bg-brand text-white'
                : item.key === 'deletion' && count ? 'bg-status-red-bg text-status-red-ink' : 'bg-[color:var(--status-gray-bg)] text-ink-2')}>
                {formatNumber(count)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <SfCard data-testid="invoice-register">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line-2 px-4 sm:px-[18px]">
        {tabs}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {qbMap ? (
            <button
              type="button"
              aria-pressed={qbDiffersOnly}
              disabled={!qbDiffersCount && !qbDiffersOnly}
              onClick={toggleQbDiffers}
              data-testid="register-qbd-differs"
              title={`Invoices QuickBooks shows as paid or still open where this one is not, with another amount or for another customer${qbMatches.last_success_at ? ` · read from QuickBooks ${formatTimeAgo(qbMatches.last_success_at)}` : ''}`}
              className={cn(
                'inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-semibold transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                qbDiffersOnly ? 'border-brand bg-brand-soft text-ink' : 'border-line bg-surface text-ink-2 hover:border-[color:var(--ink-4)]',
                !qbDiffersOnly && qbDiffersCount > 0 && 'border-[color:var(--status-amber-bg)] bg-status-amber-bg text-status-amber-ink'
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-status-amber" aria-hidden="true" />
              Differs from QuickBooks <b className="tabular-nums">{formatNumber(qbDiffersCount)}</b>
            </button>
          ) : null}
          <Segmented
            size="sm"
            ariaLabel="Base or variation invoices"
            value={kind}
            onChange={go(setKind)}
            testId="register-kind"
            options={[
              { key: 'all', label: 'All', count: kindCounts.all },
              { key: 'base', label: 'Base', count: kindCounts.base, testId: 'register-kind-base' },
              { key: 'vo', label: 'VO', count: kindCounts.vo, testId: 'register-kind-vo' },
            ]}
          />
        </div>
      </div>
      {readyIds && (view === 'ready' || view === 'notStarted') ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-2 px-4 py-2.5 sm:px-[18px]" data-testid="register-not-raised">
          <Segmented
            size="sm"
            ariaLabel="Milestones not invoiced"
            value={view}
            onChange={go(setView)}
            testId="register-not-raised-switch"
            options={[
              { key: 'ready', label: 'Ready to bill', count: counts.views.ready, testId: 'register-subview-ready', title: 'The work is done in Execution' },
              { key: 'notStarted', label: 'All not raised', count: counts.views.notStarted, testId: 'register-view-notStarted', title: 'Every scheduled payment milestone not invoiced yet' },
            ]}
          />
          <span className="ml-auto text-[12px] text-ink-3">{view === 'ready' ? 'Work done in Execution · biggest first' : 'Ready to bill first'}</span>
        </div>
      ) : null}
      {view === 'awaiting' && counts.awaitingCount ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-line-2 px-4 py-2.5 sm:px-[18px]" data-testid="register-aging">
          <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Aging</span>
          {AGING.map((bucket) => {
            const info = counts.aging[bucket.key];
            const on = aging === bucket.key;
            return (
              <button
                key={bucket.key}
                type="button"
                aria-pressed={on}
                disabled={!info.count && !on}
                onClick={() => go(setAging)(on ? null : bucket.key)}
                data-testid={`register-aging-${bucket.key}`}
                className={cn(
                  'inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-semibold transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on ? 'border-brand bg-brand-soft text-ink' : 'border-line bg-surface text-ink-2 hover:border-[color:var(--ink-4)]',
                  bucket.key === 'over90' && info.count && !on && 'border-[color:var(--status-red-bg)] bg-status-red-bg text-status-red-ink'
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', AGING_DOT[bucket.key])} aria-hidden="true" />
                {bucket.label} <b className="tabular-nums">{formatNumber(info.count)}</b>
                {info.count ? <span className="font-medium tabular-nums opacity-80">AED {formatNumber(info.amount, { decimals: 0 })}</span> : null}
              </button>
            );
          })}
          <span className="ml-auto text-[12px] text-ink-3">Oldest first</span>
        </div>
      ) : null}

      {!list.length ? (
        <EmptyState
          icon={filtered || aging || qbFilterOn ? SearchX : ReceiptText}
          title={filtered || aging || qbFilterOn ? 'No invoices match these filters' : view === 'awaiting' ? 'Nothing is awaiting payment' : view === 'ready' ? 'Nothing is ready to raise'
            : view === 'deletion' ? 'No deletion requests' : 'No invoices here yet'}
          text={filtered || aging || qbFilterOn ? 'Try another view or remove a filter.' : view === 'awaiting' ? 'Every raised invoice has been paid.'
            : view === 'ready' ? 'A milestone shows here when its work is done in Execution and it has not been invoiced.'
              : view === 'deletion' ? 'Every request has been approved, rejected or cancelled.' : 'Invoices are raised from each project’s Invoicing tab.'}
          action={aging ? <Button variant="outline" size="sm" onClick={() => setAging(null)}>Show every age</Button>
            : qbFilterOn ? <Button variant="outline" size="sm" onClick={toggleQbDiffers}>Show every invoice</Button>
              : filtered && onClearFilters ? <Button variant="outline" size="sm" onClick={onClearFilters}>Clear filters</Button> : null}
          data-testid="register-empty"
        />
      ) : isPhone ? (
        <ul className="divide-y divide-line-2" data-testid="register-rows">
          {shown.map((row) => (
            <li key={row.key} className={cn('flex items-center gap-3 px-4 py-3', row.id === highlightId && 'bg-brand-soft')} data-testid={`register-row-${row.id}`}>
              <button type="button" onClick={() => openRow(row)} className="min-w-0 flex-1 text-left">
                <ProjectCell row={row} showDeletion />
                <span className="mt-1 block truncate text-[12px] text-ink-3">
                  {row.reference ? <span className="font-mono text-ink-2">{row.reference}</span> : 'No ref.'} · {row.milestone}
                </span>
                {qbMatchOf(qbMap, row) ? (
                  <span className="mt-1 flex min-w-0"><QbMatchChip match={qbMatchOf(qbMap, row)} testId={`register-qbd-${row.id}`} /></span>
                ) : null}
              </button>
              <span className="shrink-0 text-right">
                <span className="block text-[13px] font-semibold tabular-nums text-ink">{n2(row.amount)}</span>
                {row.bucket === 'awaiting' && row.ageDays !== null ? <span className={cn('block text-[12px]', AGE_TONE[row.aging])}>{formatNumber(row.ageDays)} d</span> : null}
              </span>
              <RowActions label={`Actions for invoice ${row.reference || row.milestone}`} items={menu(row)} />
            </li>
          ))}
        </ul>
      ) : (
        <TableScroll>
          <table className={cn(tableClass, 'table-fixed', qbMap ? 'min-w-[770px]' : 'min-w-[720px]')} data-testid="register-rows">
            <colgroup>
              <col /><col style={{ width: 76 }} /><col style={{ width: 132 }} /><col style={{ width: 112 }} />
              {/* QuickBooks Desktop on: room for the match chip under the QuickBooks Online state */}
              <col style={{ width: 112 }} /><col style={{ width: qbMap ? 168 : 118 }} /><col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}>Project</th>
                <th className={thClass}>Invoice</th>
                <th className={thClass}>Milestone</th>
                <th className={cn(thClass, 'text-right')}>Amount</th>
                <th className={thClass} aria-sort={view === 'awaiting' ? 'descending' : undefined}>
                  <span className="inline-flex items-center gap-1">{view === 'received' ? 'Paid' : 'Submitted'}{view === 'awaiting' ? <ArrowDown className="h-3 w-3" aria-label="oldest first" /> : null}</span>
                </th>
                <th className={thClass}>QuickBooks</th>
                <th className={thClass}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <Fragment key={row.key}>
                <tr
                  className={cn(
                    'group cursor-pointer hover:[&>td]:bg-surface-hover',
                    row.id === highlightId && '[&>td]:bg-brand-soft',
                    row.deletionRequest && '[&>td]:border-b-0',
                  )}
                  onClick={() => openRow(row)}
                  data-testid={`register-row-${row.id}`}
                  data-highlighted={row.id === highlightId ? 'true' : undefined}
                >
                  <td className={cn(tdClass, 'h-[54px] py-1.5')}><ProjectCell row={row} /></td>
                  <td className={cn(tdClass, 'truncate font-mono text-[12.5px] font-semibold')}>{row.reference || <Dash label="No invoice number" />}</td>
                  <td className={cn(tdClass, 'text-[12.5px]')}>
                    <span className="block truncate">{row.milestone}</span>
                    {row.split ? <span className="block text-[11.5px] text-ink-3">split {row.split}</span> : null}
                    {isReady(row) ? (
                      <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1">
                        <span className="inline-flex h-[18px] items-center gap-1 rounded-md bg-status-lime-bg px-1.5 text-[11px] font-semibold text-status-lime-ink" title={readyItem(row)?.evidence?.join(' · ') || 'The work behind this milestone is done in Execution'} data-testid={`register-ready-${row.id}`}>
                          <Zap className="h-3 w-3" aria-hidden="true" />Ready to bill
                        </span>
                        {readyItem(row)?.project_status === 'On Hold' ? <StatusPill status="On Hold" domain="project" size="sm" data-testid={`ready-to-bill-on-hold-${row.id}`} /> : null}
                      </span>
                    ) : null}
                    {view === 'ready' && readyItem(row)?.evidence?.length ? (
                      <span className="mt-0.5 block truncate text-[11.5px] text-ink-3" title={readyItem(row).evidence.join(' · ')}>{readyItem(row).evidence.join(' · ')}</span>
                    ) : null}
                  </td>
                  <td className={cn(tdClass, numClass, 'whitespace-nowrap font-semibold')}>{n2(row.amount)}</td>
                  <td className={cn(tdClass, 'py-1.5')}><SubmittedCell row={row} /></td>
                  <td className={cn(tdClass, qbMap && 'py-1.5')}>
                    <QuickBooksCell row={row} />
                    {qbMatchOf(qbMap, row) ? (
                      <span className="mt-1 flex min-w-0"><QbMatchChip match={qbMatchOf(qbMap, row)} testId={`register-qbd-${row.id}`} /></span>
                    ) : null}
                  </td>
                  <td className={cn(tdClass, 'text-right')} onClick={(event) => event.stopPropagation()}>
                    <RowActions label={`Actions for invoice ${row.reference || row.milestone}`} items={menu(row)} data-testid={`register-menu-${row.id}`} />
                  </td>
                </tr>
                {row.deletionRequest ? (
                  <tr
                    className={cn('cursor-pointer', row.id === highlightId && '[&>td]:bg-brand-soft')}
                    onClick={() => openRow(row)}
                    data-testid={`register-deletion-${row.id}`}
                  >
                    <td colSpan={7} className="border-b border-line-2 px-4 pb-2.5 pt-0 sm:px-[18px]">
                      <DeletionLine row={row} approver={deletion.isApprover} />
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {list.length ? (
        <CardFoot>
          <span>
            Showing {formatNumber(shown.length)} of {formatNumber(list.length)}
            {pageCount > current ? <> · <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => setPage(current + 1)}>{formatNumber(list.length - current * PAGE_SIZE)} more</button> · AED {formatNumber(hiddenAmount, { decimals: 0 })}</> : null}
          </span>
          <span className="flex items-center gap-3">
            <b className="font-semibold tabular-nums text-ink" data-testid="register-total">Total {formatMoney(total)}</b>
            <Pager page={current} pageCount={pageCount} onPage={setPage} label="Invoice pages" />
          </span>
        </CardFoot>
      ) : null}
      {view === 'awaiting' && counts.estimatedDates ? (
        <p className="flex items-start gap-1.5 border-t border-line-2 px-4 py-2 text-[11.5px] text-ink-3 sm:px-[18px]" data-testid="register-estimated-note">
          <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
          ≈ = the submitted day was not recorded for older invoices; their age counts from the last change. New submissions record the exact day.
        </p>
      ) : null}
    </SfCard>
  );
}
